/**
 * Reviewer (buyer) approval contracts and API helpers (plan 04 section 4.3, WP-REV-08).
 *
 * Mirrors `app/schemas/ap_review.py` and the routes in `app/routers/ap_review.py`.
 * AP-side calls live under `/api/invoices/{id}/...` and `/api/ap/reviewers`;
 * reviewer calls under `/api/review/*`. Kept apart from `lib/ap/workbench.ts`
 * so the Workbench client is not touched (plan 04 section 3.5).
 *
 * Round-3 decisions: a buyer *reject* returns the invoice to Needs Review (A3),
 * and the buyer gets an in-app notification (A4) through the
 * `/api/review/notifications*` routes.
 */

import { apiClient } from '@/lib/api-client'
import type { InvoiceStatus } from '@/lib/ap/invoices'
import type { ActionResult } from '@/lib/ap/workbench'
import { normaliseNotifications } from '@/lib/review-view'
import type { ReviewNotificationList } from '@/lib/review-view'

export type {
  ReviewNotification,
  ReviewNotificationKind,
  ReviewNotificationList,
} from '@/lib/review-view'

// ─── Shared vocabulary ─────────────────────────────────────────────────────

/** `ReviewAssignmentStatus` in `app/models/ap_review.py`. */
export type AssignmentStatus = 'open' | 'approved' | 'rejected' | 'info_requested' | 'sent_back' | 'cancelled'

/** Plan 10 C6: `hold` keeps the request open (marked on hold); `send_back` returns it to AP Needs Review. */
export type ReviewDecision = 'approve' | 'reject' | 'request_info' | 'hold' | 'send_back'

/** Queue filter (`?filter=`), plan 04 section 4.3 item 7. */
export type QueueFilter = 'waiting' | 'decided' | 'all'

export type RoutedBy = 'policy' | 'manual'

/** The fired DOA policy (DOA routing is Phase 2, master plan 4.7, so usually absent). */
export interface PolicyRef {
  id: string | null
  rule_code: string | null
  name: string | null
  text?: string | null
}

/** One approval round (`AssignmentOut`). */
export interface AssignmentOut {
  id: number
  invoice_db_id: number
  invoice_number: string | null
  round: number
  status: AssignmentStatus
  reviewer_key: string | null
  reviewer_name: string | null
  reviewer_title: string | null
  reviewer_role: string | null
  doa_level: string | null
  routed_by: RoutedBy | string
  policy_id: string | null
  policy_rule_code: string | null
  request_note: string | null
  /** Display text, e.g. "Priya Raman (AP Manager)". */
  requested_by: string | null
  requested_at: string | null
  decision_comment: string | null
  /** The status the decision moved the invoice to (A3: reject lands in needs_review). */
  resulting_invoice_status: InvoiceStatus | string | null
  decided_at: string | null
  decided_by: string | null
  /** A4 in-app notification stamps. */
  reviewer_notified_at?: string | null
  reviewer_seen_at?: string | null
  requester_seen_at?: string | null
  /** The brief headline, for the queue and the notification list. */
  headline?: string | null
  /** One plain-English line for the AP banner ("Rejected by buyer Jordan Lee: ..."). */
  banner: string
  /** Set while the approver holds the open round (plan 10 C6). */
  held_at?: string | null
  hold_reason?: string | null
  version: number
}

// ─── AP side ───────────────────────────────────────────────────────────────

/** A directory entry as the AP picker sees it (email masked). */
export interface ReviewerDirectoryEntry {
  reviewer_key: string
  name: string
  title: string | null
  role: string
  /** Display name for the role from Settings `ap.review.role_labels` ("AP Manager"). */
  role_label?: string | null
  roles?: string[]
  email_masked: string | null
}

export interface ReviewerSuggestion {
  reviewer_key: string
  name: string
  title: string | null
  /** Masked. */
  email: string | null
  role: string
  doa_level: string | null
  policy: PolicyRef | null
}

/** GET /api/invoices/{id}/approval-route. `suggestion` is null when no DOA policy fires. */
export interface ApprovalRouteOut {
  invoice_id: number
  suggestion: ReviewerSuggestion | null
  /** A role a policy chose that nobody in the directory holds. */
  unresolved_role?: string | null
  alternatives: ReviewerDirectoryEntry[]
  trace: { step: string; rule_code?: string | null; status: string; message: string | null }[]
}

/** GET /api/invoices/{id}/approvals. */
export interface InvoiceApprovalsOut {
  can_request: boolean
  open: AssignmentOut | null
  history: AssignmentOut[]
}

export interface RequestApprovalIn {
  reviewer_key: string
  note?: string | null
  version: number
  routed_by: RoutedBy
  policy_id?: string | null
  doa_level?: string | null
}

/** POST /api/invoices/{id}/actions/request-approval: the Workbench ActionResult plus the new round. */
export interface ApprovalActionResult extends ActionResult {
  assignment: AssignmentOut
}

export interface ReviewersOut {
  items: ReviewerDirectoryEntry[]
}

// ─── Reviewer side ─────────────────────────────────────────────────────────

/** GET /api/review/summary. `oversight` is true for admin and AP-Manager (they see every request). */
export interface ReviewSummary {
  waiting: number
  decided_today: number
  /** Approver briefing (plan 10 C6). */
  on_hold?: number
  decided_this_week?: number
  decided_total?: number
  oldest_waiting_at?: string | null
  unread: number
  reviewer: { name: string | null; title: string | null; role: string | null; role_label?: string | null }
  oversight: boolean
}

/** One queue row (`GET /api/review/assignments`). */
export interface ReviewQueueItem {
  id: number
  status: AssignmentStatus
  round?: number
  invoice_db_id: number
  invoice_number: string | null
  vendor_name: string | null
  amount: number | null
  currency: string | null
  due_date: string | null
  headline: string | null
  doa_level: string | null
  reviewer_name?: string | null
  reviewer_title?: string | null
  reviewer_role?: string | null
  requested_by: string | null
  requested_at: string | null
  decided_at: string | null
  held_at?: string | null
  unread?: boolean
}

/** Rule 08 pagination envelope. */
export interface Paginated<T> {
  data: T[]
  pagination: { page: number; page_size: number; total_items: number; total_pages: number }
}

export interface ReviewBrief {
  headline: string
  why_you: string | null
  points: string[]
  source: 'ai' | 'template'
  /** The AI Employee that wrote the summary (A1 label, e.g. "Matching Agent"). */
  ai_employee: string | null
}

export type EvidenceValue = string | number | boolean | null

export interface EvidenceRow {
  label: string
  invoice_value: EvidenceValue
  po_value: EvidenceValue
  grn_value: EvidenceValue
  differs: boolean
}

export interface ReviewException {
  title: string
  status?: string
  plain_text: string | null
  rows: EvidenceRow[]
  detail: string | null
}

export interface LineDifference {
  line: number
  description: string
  rows: EvidenceRow[]
}

export interface ReviewEvidence {
  /** `passed`, `warnings`, `failed` (other keys ignored). */
  counts: Record<string, number>
  exceptions: ReviewException[]
  line_differences?: LineDifference[]
  document_ids?: Record<string, unknown>
}

export interface ReviewHistoryEntry {
  at: string | null
  actor: string | null
  text: string
  /** "via email" on the received entry when the invoice came through the mailbox. */
  source_chip: string | null
}

export interface ReviewSiblings {
  index: number
  total: number
  prev_id: number | null
  next_id: number | null
}

/** One invoice, PO or GRN line as the buyer sees it. */
export interface ReviewLine {
  description: string
  sku: string | number | null
  quantity: number | null
  unit: string | null
  unit_price: number | null
  total: number | null
}

/** The invoice header the buyer sees (allow-listed; no trace, no confidence, no DSL). */
export interface ReviewInvoice {
  id: number
  invoice_number: string | null
  vendor_name: string | null
  invoice_date: string | null
  due_date: string | null
  currency: string | null
  subtotal: number | null
  tax: number | null
  grand_total: number | null
  status: InvoiceStatus | string
  has_file: boolean
  po_numbers: string[]
  grn_numbers: string[]
  line_items: ReviewLine[]
  line_items_total?: number
}

/** A PO or GRN for the structured viewer. */
export interface ReviewDocument {
  number: string | null
  vendor_name: string | null
  date: string | null
  grand_total: number | null
  is_synthetic?: boolean
  line_items: ReviewLine[]
}

/** GET /api/review/assignments/{id}. Opening it marks the request seen for the named reviewer (A4). */
export interface ReviewDetailOut {
  assignment: AssignmentOut
  invoice: ReviewInvoice
  related_pos: ReviewDocument[]
  related_grns: ReviewDocument[]
  brief: ReviewBrief | null
  /** One plain line built from the evidence (plan 10 C6); the brief is the optional "More detail". */
  ask?: string | null
  evidence: ReviewEvidence | null
  history: ReviewHistoryEntry[]
  siblings: ReviewSiblings | null
  can_decide: boolean
  /** False for an oversight viewer (admin / AP-Manager): the page names the approver instead of "you". */
  is_reviewer?: boolean
  /** Settings `ap.review.comment_min_length`. */
  comment_min_length?: number | null
  /** Settings `ap.review.comment_required_for`: decisions that need a comment. */
  comment_required_for?: ReviewDecision[] | null
}

export interface DecisionIn {
  decision: ReviewDecision
  comment?: string | null
  version: number
}

/** `NotificationItem` in `app/schemas/ap_review.py` (reviewer bell and AP requester notifications, A4). */
export interface NotificationItemOut {
  assignment_id: number
  invoice_db_id: number
  invoice_number: string | null
  vendor_name: string | null
  status: AssignmentStatus | string
  text: string
  at: string | null
  unread: boolean
}

/** `NotificationsOut`: `GET /api/review/notifications` and `GET /api/ap/approval-notifications`. */
export interface NotificationsOut {
  unread_count: number
  items: NotificationItemOut[]
}

/** `MarkSeenOut`: `POST .../notifications/seen`. */
export interface MarkSeenOut {
  updated: number
  unread_count: number
}

export interface DecisionOut {
  assignment: AssignmentOut
  invoice_status: InvoiceStatus | string
  message: string
}

// ─── API ───────────────────────────────────────────────────────────────────

const inv = (id: number) => `/api/invoices/${id}`

function query(params: Record<string, string | number | boolean | null | undefined>): string {
  const qs = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value === null || value === undefined || value === '') continue
    qs.set(key, String(value))
  }
  const text = qs.toString()
  return text ? `?${text}` : ''
}

/** AP-side calls used by the Workbench (Send for Approval, status banner). */
export const approvalsApi = {
  route: (invoiceId: number) => apiClient.get<ApprovalRouteOut>(`${inv(invoiceId)}/approval-route`),
  list: (invoiceId: number) => apiClient.get<InvoiceApprovalsOut>(`${inv(invoiceId)}/approvals`),
  request: (invoiceId: number, body: RequestApprovalIn) =>
    apiClient.post<ApprovalActionResult>(`${inv(invoiceId)}/actions/request-approval`, body),
  cancel: (invoiceId: number, body: { reason?: string | null; version?: number | null }) =>
    apiClient.post<ApprovalActionResult>(`${inv(invoiceId)}/actions/cancel-approval`, body),
  reviewers: () => apiClient.get<ReviewersOut>('/api/ap/reviewers'),
  /** The AP requester's in-app notifications: decisions on the rounds they sent (A4). */
  notifications: async (): Promise<ReviewNotificationList> =>
    normaliseNotifications(await apiClient.get<NotificationsOut>('/api/ap/approval-notifications')),
  markSeen: (assignmentIds?: number[]) =>
    apiClient.post<MarkSeenOut>(
      '/api/ap/approval-notifications/seen',
      assignmentIds?.length ? { assignment_ids: assignmentIds } : {}
    ),
}

/** Reviewer-side calls used by `/review` and `/review/[id]`. */
export const reviewApi = {
  summary: () => apiClient.get<ReviewSummary>('/api/review/summary'),
  /** The server scopes the queue by role: a reviewer sees their own rounds, admin and AP-Manager see all. */
  queue: (params: { filter: QueueFilter; page?: number; page_size?: number }) =>
    apiClient.get<Paginated<ReviewQueueItem>>(
      `/api/review/assignments${query({ filter: params.filter, page: params.page ?? 1, page_size: params.page_size ?? 50 })}`
    ),
  detail: (assignmentId: number, filter?: QueueFilter | null) =>
    apiClient.get<ReviewDetailOut>(`/api/review/assignments/${assignmentId}${query({ filter: filter ?? undefined })}`),
  decide: (assignmentId: number, body: DecisionIn) =>
    apiClient.post<DecisionOut>(`/api/review/assignments/${assignmentId}/decision`, body),
  notifications: async (): Promise<ReviewNotificationList> =>
    normaliseNotifications(await apiClient.get<NotificationsOut>('/api/review/notifications')),
  /** The bell and badge count: `unread_count` on `GET /api/review/notifications` (there is no separate count route). */
  unreadCount: async (): Promise<number> =>
    (await apiClient.get<NotificationsOut>('/api/review/notifications')).unread_count,
  markSeen: (assignmentIds?: number[]) =>
    apiClient.post<MarkSeenOut>('/api/review/notifications/seen', assignmentIds?.length ? { assignment_ids: assignmentIds } : {}),
}

/** The reviewer-scoped invoice PDF (plan 04 item 9). Fetched raw with a bearer token, never via apiClient. */
export function reviewInvoiceFileUrl(assignmentId: number): string {
  const apiUrl = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:8001'
  const basePath = process.env.NEXT_PUBLIC_BASE_PATH || ''
  return `${apiUrl}${basePath}/api/review/assignments/${assignmentId}/invoice-file`
}
